import { useEffect, useMemo, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@verlo/shared/components/ui/dialog';
import { Input } from '@verlo/shared/components/ui/input';
import { Signal } from '@verlo/shared/components/ui/signal';
import { cn } from '@verlo/shared/lib/utils';
import { cellColumns } from '@verlo/shared/lib/cellColumns';
import { modifiersApi } from '@verlo/shared/services/api';
import { AlertTriangle, MessageSquare, Minus, Plus, Search, X } from 'lucide-react';
import { SIZE_GROUP, sizeGroupOf, takeSize } from '../lib/sizeChoice';

/**
 * The modifier sheet (modifiers plan, signed off 21 Sep 2026). Opens on a long press on
 * the bar or the table pad, or on every tap of an item marked `always_ask`.
 *
 * Three registers, told apart by ground, type and one dashed line — no section labels:
 *   the item, on paper       name, what's in it, allergens, the ×
 *   the choices, on white    choose-one groups as joined cells (exactly one solid navy;
 *                            each shows the price the drink *becomes*), an optional double
 *                            under a spirit group, extras as detached tick tiles (tint +
 *                            tick, a counted tile becomes its own quiet stepper), and
 *                            "Also" — one box that searches the whole library, with free
 *                            text as a £0 note or, only where the till has priced free
 *                            text on, an extra with a stepper price
 *   the ticket, torn off     the bar ticket in the bar's words, price in the Add bar only
 *
 * Nothing here decides a price: the sheet shows what the server will charge (`price` on
 * an option is a delta on the item's base; `double_price` on top), and the order line
 * carries modifier *objects* — `{option_id | modifier_id | custom, name, qty, double,
 * price}` — which `orders._price_lines` re-derives. Every target is ≥ 44px.
 */

const p = (v) => Math.round((parseFloat(v) || 0) * 100);
/** Namespace for a meal component's option id, so it can never collide with a
 *  modifier option's. Stripped again when the line's components are built. */
const MEAL = 'meal:';
const pounds = (pence) => pence / 100;

/** What the ticket and the cart say for a list of modifier objects (mirrors `modifier_lines.ticket_words`). */
export const ticketWords = (mods) => (mods || []).map((m) => {
  if (typeof m === 'string') return m;
  let name = m.name || '';
  if (m.double) name = `${name}, double`;
  if ((m.qty || 1) > 1) name = `${m.qty}× ${name}`;
  if (m.custom && !p(m.price)) name = name.toUpperCase();
  return name;
});

const isDrinkCategory = (c) => ['lagers', 'wines', 'cocktails', 'soft_drinks', 'drinks', 'kids_drinks', 'shakes', 'beverages', 'spirits', 'beers'].includes((c || '').toLowerCase());

// --- pieces ------------------------------------------------------------------------------

/** A choose-one group: joined cells, one filled. Price shown is what the drink becomes —
 *  except on a meal's group, where it is the delta: a meal has several groups, and an
 *  absolute price on one cell would be a number the guest never pays. */
const Cells = ({ group, base, value, onChange, formatCurrency }) => {
  const priced = group.options.some((o) => p(o.price) !== 0);
  const asDelta = !!group.meal;
  const n = group.options.length;
  const cols = cellColumns(n);
  // The grid's hairlines are its background showing through the gaps, so a short last row
  // would show grey where there are no choices (the flat iron's five, Oct 2026): fill it white.
  const blanks = (cols - (n % cols)) % cols;
  return (
    <div className="grid gap-px border border-hairline-strong bg-hairline-strong" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }} role="radiogroup" aria-label={group.label} data-testid={`sheet-group-${group.id}`}>
      {group.options.map((o) => {
        const on = value === o.id;
        return (
          <button key={o.id} type="button" role="radio" aria-checked={on} onClick={() => onChange(o.id)}
            className={cn('grid min-h-[52px] content-center justify-items-center gap-px px-1.5 py-1 text-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-verlo',
              on ? 'bg-verlo text-white' : 'bg-white text-ink hover:bg-surface-2')}
            data-testid={`sheet-option-${o.id}`}>
            <span className="text-[13.5px] font-bold leading-[1.15]">{o.name}</span>
            {priced && (asDelta ? p(o.price) !== 0 : true) && (
              <span className={cn('font-mono text-[11.5px] font-semibold tabular-nums', on ? 'text-white/75' : 'text-ink-faint')}>
                {asDelta ? `+${formatCurrency(pounds(p(o.price)))}` : formatCurrency(pounds(base + p(o.price)))}
              </span>
            )}
          </button>
        );
      })}
      {Array.from({ length: blanks }, (_, i) => <span key={`blank-${i}`} className="bg-white" aria-hidden data-testid="sheet-cell-blank" />)}
    </div>
  );
};

/** The quiet stepper: − and + on the pale navy, the count in ink, 44px targets. */
const Stepper = ({ value, onChange, min = 1, format = (v) => `×${v}`, size = 44, testId }) => (
  <span className="inline-grid h-11 shrink-0" style={{ gridTemplateColumns: `${size}px ${Math.max(40, size - 4)}px ${size}px`, height: size }} data-testid={testId}>
    <button type="button" onClick={() => onChange(value - 1)} className="grid place-items-center bg-[#DCD8F2] text-verlo focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-verlo" aria-label="Fewer"><Minus size={18} strokeWidth={2.5} /></button>
    <span className="grid place-items-center font-mono text-[14px] font-extrabold tabular-nums text-ink">{format(value)}</span>
    <button type="button" onClick={() => onChange(value + 1)} className="grid place-items-center bg-[#DCD8F2] text-verlo focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-verlo" aria-label="More"><Plus size={18} strokeWidth={2.5} /></button>
  </span>
);

/** A tick tile: detached, tint + tick when on; a counted one grows a stepper. */
const Tile = ({ name, price, on, qty, onToggle, onQty, formatCurrency, wide, sub, testId }) => {
  const line = p(price) * (qty || 1);
  return (
    <div className={cn('grid min-h-[52px] items-center gap-2 border transition-colors', on ? 'border-verlo bg-verlo-tint' : 'border-hairline-strong bg-white',
      wide && 'col-span-2', on && onQty ? 'grid-cols-[1fr_auto] pr-1' : 'grid-cols-[1fr_auto] pr-2.5')} data-testid={testId} data-on={on ? '1' : '0'}>
      <button type="button" onClick={onToggle} aria-pressed={on} className="grid min-h-[50px] content-center justify-items-start py-1 pl-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-verlo">
        <span className={cn('text-[13.5px] font-bold leading-[1.15]', on ? 'text-verlo' : 'text-ink')}>{name}</span>
        {(p(price) !== 0 || sub) && (
          <span className="mt-px font-mono text-[11.5px] font-semibold tabular-nums text-ink-faint">
            {sub || `${line > 0 ? '+' : '−'}${formatCurrency(pounds(Math.abs(line)))}`}
          </span>
        )}
      </button>
      {on && onQty ? (
        <Stepper value={qty} onChange={(v) => (v <= 0 ? onToggle() : onQty(v))} testId={`${testId}-qty`} />
      ) : (
        <button type="button" onClick={onToggle} aria-hidden tabIndex={-1} className={cn('grid h-5 w-5 place-items-center border-[1.5px] text-[12px] font-extrabold', on ? 'border-verlo bg-verlo text-white' : 'border-hairline-strong bg-white')}>{on ? '✓' : ''}</button>
      )}
    </div>
  );
};

// --- the sheet -----------------------------------------------------------------------------

/**
 * `library` / `config` may be passed in (the QR menu, where a guest has no token and no
 * "Also"); otherwise the sheet reads them itself. `allowAlso={false}` hides the search and
 * free text altogether — a guest picks from the groups only.
 */
const ModifierSheet = ({ open, onOpenChange, item, onConfirm, formatCurrency, initial = null, confirmLabel = 'Add to order',
  library: libraryProp = null, config: configProp = null, allowAlso = true }) => {
  // A sized item's sizes come first, as a choose-one "Size" (lib/sizeChoice).
  const modifierGroups = useMemo(() => {
    const size = sizeGroupOf(item);
    return [...(size ? [size] : []), ...(item?.modifier_groups || [])];
  }, [item]);
  // A meal's groups (a kids meal, a lunch combo) draw as the same two shapes — joined cells
  // for a choose-one, tick tiles for an optional — so they are normalised into the same
  // record rather than given a second sheet. The option id is the component's menu item,
  // namespaced so it can never collide with a modifier option's id.
  const mealGroups = useMemo(() => (item?.meal_groups || []).map((g) => ({
    ...g,
    meal: true,
    options: (g.options || []).map((o) => ({
      ...o, id: `${MEAL}${o.menu_item_id}`, price: o.price_delta || 0,
    })),
  })), [item]);
  const groups = useMemo(() => [...mealGroups, ...modifierGroups], [mealGroups, modifierGroups]);
  const [ones, setOnes] = useState({});        // groupId -> optionId
  const [doubles, setDoubles] = useState({});  // groupId -> bool
  const [anys, setAnys] = useState({});        // optionId -> qty
  const [also, setAlso] = useState([]);        // [{modifier_id|custom, name, price, qty}]
  const [alsoOpen, setAlsoOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [freePrice, setFreePrice] = useState(0.5);
  const [library, setLibrary] = useState([]);
  const [config, setConfig] = useState({ free_text_pricing: false, free_text_cap: 5, free_text_step: 0.5 });
  const [recent, setRecent] = useState(() => { try { return JSON.parse(localStorage.getItem('verlo.modifiers.recent') || '[]'); } catch { return []; } });
  const searchRef = useRef(null);

  // The library and the till's free-text setting: read when the sheet first opens, unless
  // the caller supplied them (the QR menu has no token to read with).
  useEffect(() => {
    if (!open) return;
    if (libraryProp) { setLibrary(libraryProp); } else {
      modifiersApi.getAll().then(({ data }) => setLibrary((data || []).filter((m) => m.is_active !== false))).catch(() => {});
    }
    if (configProp) { setConfig(configProp); } else {
      modifiersApi.config().then(({ data }) => setConfig(data)).catch(() => {});
    }
  }, [open, libraryProp, configProp]);

  // Defaults, or the line being edited.
  useEffect(() => {
    if (!open) return;
    const o = {}; const d = {}; const a = {}; const extra = [];
    for (const g of groups) {
      if (g.rule === 'one') {
        const def = g.options.find((x) => x.default) || g.options[0];
        if (def) o[g.id] = def.id;
      }
    }
    // `initial` is the line being edited: historically just its modifiers, and since the
    // sheet learned to carry a message, optionally `{modifiers, notes, allergy_notes}`.
    // A meal being edited: its components choose their own groups back.
    for (const c of (Array.isArray(initial) ? [] : initial?.components) || []) {
      const g = mealGroups.find((gg) => gg.id === c.group)
        || mealGroups.find((gg) => gg.options.some((x) => x.menu_item_id === c.menu_item_id));
      if (!g) continue;
      if ((g.rule || 'one') === 'one') o[g.id] = `${MEAL}${c.menu_item_id}`;
      else a[`${MEAL}${c.menu_item_id}`] = c.quantity || 1;
    }
    // A sized line being edited: its size, chosen back.
    const sized = !Array.isArray(initial) && initial?.size
      && groups.find((gg) => gg.id === SIZE_GROUP)?.options.find((x) => x.name === initial.size);
    if (sized) o[SIZE_GROUP] = sized.id;
    for (const m of (Array.isArray(initial) ? initial : initial?.modifiers) || []) {
      if (typeof m !== 'object') continue;
      if (m.option_id) {
        const g = groups.find((gg) => gg.options.some((x) => x.id === m.option_id));
        if (!g) continue;
        if (g.rule === 'one') { o[g.id] = m.option_id; if (m.double) d[g.id] = true; } else a[m.option_id] = m.qty || 1;
      } else if (m.modifier_id || m.custom) {
        extra.push({ modifier_id: m.modifier_id, custom: !!m.custom, name: m.name, price: m.price || 0, qty: m.qty || 1, audit_id: m.audit_id });
      }
    }
    setOnes(o); setDoubles(d); setAnys(a); setAlso(extra); setAlsoOpen(extra.length > 0); setQuery(''); setFreePrice(config.free_text_step || 0.5);
  }, [open, groups, mealGroups, initial]); // eslint-disable-line react-hooks/exhaustive-deps

  const base = p(item?.price);

  // What the meal is made of, in the menu's own order. The server re-resolves all of it
  // against the item's groups — this is what the guest picked, not what they are charged.
  const components = useMemo(() => {
    const out = [];
    for (const g of mealGroups) {
      if ((g.rule || 'one') === 'one') {
        const opt = g.options.find((x) => x.id === ones[g.id]);
        if (opt) out.push({ group: g.id, menu_item_id: opt.menu_item_id, name: opt.name, quantity: 1, price_delta: opt.price || 0 });
      } else {
        for (const opt of g.options) {
          const q = anys[opt.id];
          if (q) out.push({ group: g.id, menu_item_id: opt.menu_item_id, name: opt.name, quantity: q, price_delta: opt.price || 0 });
        }
      }
    }
    return out;
  }, [mealGroups, ones, anys]);

  const mealDelta = useMemo(
    () => components.reduce((sum, c) => sum + p(c.price_delta) * (c.quantity || 1), 0),
    [components],
  );

  // The line's modifier objects, in the order the ticket reads them.
  const modifiers = useMemo(() => {
    const out = [];
    for (const g of modifierGroups) {
      if (g.rule === 'one') {
        const opt = g.options.find((x) => x.id === ones[g.id]);
        if (!opt) continue;
        // A default at £0 (Mild) stays off the ticket — "as it comes" — unless the group
        // says print it (a cooking temperature the kitchen must never guess).
        const isDefaultFree = opt.default && p(opt.price) === 0 && !doubles[g.id] && !g.print_default;
        if (!isDefaultFree) out.push({ option_id: opt.id, name: opt.name, price: p(opt.price) + (doubles[g.id] ? p(opt.double_price) : 0), qty: 1, double: !!doubles[g.id] && opt.double_price != null, group: g.label });
      } else {
        for (const opt of g.options) {
          const q = anys[opt.id];
          if (q) out.push({ option_id: opt.id, name: opt.name, price: p(opt.price) * q, qty: q, group: g.label });
        }
      }
    }
    for (const e of also) out.push({ ...e, price: p(e.price) * (e.qty || 1) });
    return out;
  }, [modifierGroups, ones, doubles, anys, also]);

  const total = base + mealDelta + modifiers.reduce((s, m) => s + m.price, 0);
  const changed = modifiers.length > 0 || components.length > 0;

  // Search: the library minus what's already on, recent first.
  const q = query.trim();
  const results = useMemo(() => {
    const onNames = new Set(modifiers.map((m) => (m.name || '').toLowerCase()));
    const pool = library.filter((m) => !onNames.has((m.name || '').toLowerCase()));
    const rank = (m) => (recent.includes(m.name) ? 0 : 1);
    if (!q) return pool.filter((m) => recent.includes(m.name)).slice(0, 4).map((m) => ({ ...m, recent: true }));
    const ql = q.toLowerCase();
    return pool.filter((m) => (m.name || '').toLowerCase().includes(ql)).sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name)).slice(0, 5).map((m) => ({ ...m, recent: recent.includes(m.name) }));
  }, [q, library, modifiers, recent]);
  const exact = results.some((r) => r.name.toLowerCase() === q.toLowerCase());

  const remember = (name) => {
    const next = [name, ...recent.filter((x) => x !== name)].slice(0, 8);
    setRecent(next);
    try { localStorage.setItem('verlo.modifiers.recent', JSON.stringify(next)); } catch { /* private mode */ }
  };
  useEffect(() => {
    if (!open) return;
    setNote(initial?.notes || '');
    setAllergyNote(initial?.allergy_notes || '');
    setNoteField(null);
  }, [open, initial]);

  const addAlso = (e) => { setAlso((prev) => [...prev, e]); remember(e.name); setQuery(''); setFreePrice(config.free_text_step || 0.5); searchRef.current?.focus(); };
  // A message for this dish (30 Sep 2026). Two-thirds of a menu has choices, so for most
  // items this sheet *is* the line editor — and until now it had nowhere to say "no onions"
  // except the free-text search, which makes a modifier rather than a note.
  const [note, setNote] = useState('');
  const [allergyNote, setAllergyNote] = useState('');
  const [noteField, setNoteField] = useState(null);   // null | 'notes' | 'allergy_notes'
  const noteRef = useRef(null);
  const step = config.free_text_step || 0.5;
  const cap = config.free_text_cap ?? 5;
  const overCap = p(freePrice) > p(cap);

  const confirm = () => {
    if (item?.available_now === false && !initial) return;   // off now: see the footer
    // What the server needs: ids and words; unit prices for custom entries only.
    const { size, modifiers: chosen } = takeSize(modifiers);
    const payload = chosen.map((m) => {
      if (m.option_id) return { option_id: m.option_id, name: m.name, qty: m.qty, double: !!m.double };
      if (m.modifier_id) return { modifier_id: m.modifier_id, name: m.name, qty: m.qty };
      return { name: m.name, price: pounds(p(m.price) / (m.qty || 1)), custom: true, ...(m.audit_id ? { audit_id: m.audit_id } : {}) };
    });
    onConfirm?.(item, payload, pounds(total - base), pounds(total),
                { notes: note.trim(), allergy_notes: allergyNote.trim(), ...(size ? { size } : {}),
                  ...(mealGroups.length ? { components, is_meal: true } : {}) });
    onOpenChange(false);
  };

  const words = ticketWords(modifiers);
  const desc = item?.description || (item?.ingredients?.length ? item.ingredients.map((i) => i.name || i).join(', ') : '');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="form" className="max-h-[94vh] gap-0 overflow-hidden p-0 [&>button]:hidden" data-testid="modifier-sheet">
        {/* the item, on paper */}
        <div className="relative border-b border-hairline-strong bg-paper px-[18px] pb-3.5 pt-[18px]">
          <button type="button" onClick={() => onOpenChange(false)} className="absolute right-2 top-2 grid h-11 w-11 place-items-center text-ink-faint hover:text-ink" aria-label="Close" data-testid="modifier-sheet-close"><X size={20} /></button>
          <DialogTitle className="pr-10 text-[22px] font-extrabold leading-[1.1] tracking-[-0.01em] text-ink">{item?.name}</DialogTitle>
          {desc && <p className="mt-1 text-[13px] text-ink-soft">{desc}</p>}
          {(item?.allergens?.length > 0 || item?.is_vegan || item?.is_vegetarian || item?.is_gluten_free) && (
            <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
              {(item.allergens || []).map((a) => <Signal key={a} tone="stop">{a}</Signal>)}
              {(item.is_vegan || item.is_vegetarian || item.is_gluten_free) && (
                <span className="ml-0.5 text-[11px] font-bold uppercase tracking-[0.04em] text-ink-faint">{[item.is_vegan ? 'vg' : item.is_vegetarian ? 'v' : null, item.is_gluten_free ? 'gf' : null].filter(Boolean).join(' · ')}</span>
              )}
            </div>
          )}
        </div>

        {/* the choices, on white */}
        <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden bg-white px-[18px] pb-3 pt-2.5">
          <div className="grid gap-3.5">
            {groups.map((g) => {
              if (g.rule === 'one') {
                const chosen = g.options.find((x) => x.id === ones[g.id]);
                const offersDouble = chosen && chosen.double_price != null;
                return (
                  <div key={g.id} className="grid gap-1.5">
                    <div className="text-[11px] font-extrabold uppercase tracking-[0.06em] text-ink-faint">{g.label}</div>
                    <Cells group={g} base={base} value={ones[g.id]} onChange={(id) => { setOnes({ ...ones, [g.id]: id }); const o = g.options.find((x) => x.id === id); if (!o || o.double_price == null) setDoubles({ ...doubles, [g.id]: false }); }} formatCurrency={formatCurrency} />
                    {offersDouble && (
                      <div className="grid grid-cols-1 gap-2">
                        <Tile name={g.double_label || 'Double measure'} price={0} sub={formatCurrency(pounds(base + p(chosen.price) + p(chosen.double_price)))}
                          on={!!doubles[g.id]} onToggle={() => setDoubles({ ...doubles, [g.id]: !doubles[g.id] })} formatCurrency={formatCurrency} testId={`sheet-double-${g.id}`} />
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <div key={g.id} className="grid gap-1.5">
                  <div className="text-[11px] font-extrabold uppercase tracking-[0.06em] text-ink-faint">{g.label}</div>
                  <div className="grid grid-cols-2 gap-2" data-testid={`sheet-group-${g.id}`}>
                    {g.options.map((o) => (
                      <Tile key={o.id} name={o.name} price={o.price} on={!!anys[o.id]} qty={anys[o.id] || 1} wide={!!anys[o.id] && o.qty}
                        onToggle={() => setAnys((prev) => { const n = { ...prev }; if (n[o.id]) delete n[o.id]; else n[o.id] = 1; return n; })}
                        onQty={o.qty ? (v) => setAnys((prev) => ({ ...prev, [o.id]: v })) : undefined}
                        formatCurrency={formatCurrency} testId={`sheet-option-${o.id}`} />
                    ))}
                  </div>
                </div>
              );
            })}

            {/* Also */}
            {/* A message for this dish. Quiet until it is wanted, because most dishes do
                not need one — and loud once an allergy is on it, because that one does. */}
            <div className="grid gap-2 border-t border-hairline pt-2.5" data-testid="sheet-message">
              {(note || allergyNote) && (
                <div className="grid gap-1">
                  {allergyNote && (
                    <button type="button" onClick={() => { setNoteField('allergy_notes'); setTimeout(() => noteRef.current?.focus(), 50); }}
                            className="flex min-h-[36px] items-center gap-2 border border-signal-stop-line bg-signal-stop-tint px-2.5 text-left text-[13.5px] font-semibold text-signal-stop"
                            data-testid="sheet-allergy-set">
                      <AlertTriangle size={14} className="shrink-0" />
                      <span className="min-w-0 flex-1 truncate">{allergyNote}</span>
                    </button>
                  )}
                  {note && (
                    <button type="button" onClick={() => { setNoteField('notes'); setTimeout(() => noteRef.current?.focus(), 50); }}
                            className="flex min-h-[36px] items-center gap-2 border border-hairline bg-surface-2 px-2.5 text-left text-[13.5px] text-ink-soft"
                            data-testid="sheet-note-set">
                      <MessageSquare size={14} className="shrink-0" />
                      <span className="min-w-0 flex-1 truncate">{note}</span>
                    </button>
                  )}
                </div>
              )}
              {noteField ? (
                <Input
                  ref={noteRef}
                  value={noteField === 'notes' ? note : allergyNote}
                  onChange={(e) => (noteField === 'notes' ? setNote(e.target.value) : setAllergyNote(e.target.value))}
                  onBlur={() => setNoteField(null)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); setNoteField(null); } }}
                  placeholder={noteField === 'notes' ? 'No onions, sauce on the side…' : 'What it must not contain'}
                  maxLength={280}
                  className={`h-11 text-[14.5px] ${noteField === 'allergy_notes' ? 'border-signal-stop-line' : 'border-hairline-strong'}`}
                  data-testid="sheet-note-input"
                />
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  <button type="button" onClick={() => { setNoteField('notes'); setTimeout(() => noteRef.current?.focus(), 50); }}
                          className="flex min-h-[44px] items-center justify-center gap-2 border border-hairline text-[14px] font-semibold text-ink-faint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-verlo"
                          data-testid="sheet-note-open">
                    <MessageSquare size={15} />{note ? 'Edit note' : 'Note'}
                  </button>
                  <button type="button" onClick={() => { setNoteField('allergy_notes'); setTimeout(() => noteRef.current?.focus(), 50); }}
                          className="flex min-h-[44px] items-center justify-center gap-2 border border-signal-stop-line text-[14px] font-semibold text-signal-stop focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal-stop-line"
                          data-testid="sheet-allergy-open">
                    <AlertTriangle size={15} />{allergyNote ? 'Edit allergy' : 'Allergy'}
                  </button>
                </div>
              )}
            </div>

            {allowAlso && <div className="grid gap-2 border-t border-hairline pt-2.5" data-testid="sheet-also">
              {also.length > 0 && (
                <div className="grid grid-cols-2 gap-2">
                  {also.map((e, i) => (
                    <Tile key={`${e.name}-${i}`} name={e.name} price={e.price} on qty={e.qty} wide={e.qty > 1 || (!!e.modifier_id && library.find((m) => m.id === e.modifier_id)?.qty)}
                      sub={e.custom && !p(e.price) ? 'note' : undefined}
                      onToggle={() => setAlso((prev) => prev.filter((_, k) => k !== i))}
                      onQty={e.modifier_id && library.find((m) => m.id === e.modifier_id)?.qty ? (v) => setAlso((prev) => prev.map((x, k) => (k === i ? { ...x, qty: v } : x))) : undefined}
                      formatCurrency={formatCurrency} testId={`sheet-also-${i}`} />
                  ))}
                </div>
              )}
              {!alsoOpen ? (
                <button type="button" onClick={() => { setAlsoOpen(true); setTimeout(() => searchRef.current?.focus(), 50); }} className="flex min-h-[44px] items-center justify-between text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-verlo" data-testid="sheet-also-open">
                  <span className="text-[14px] font-semibold text-ink-faint">{also.length ? '+ anything else' : 'Anything else…'}</span>
                  <Search size={16} className="text-ink-faint" />
                </button>
              ) : (
                <>
                  <Input ref={searchRef} value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Type to search, or say it your way…" className="h-11 border-hairline-strong text-[14.5px]" data-testid="sheet-search"
                    onKeyDown={(e) => { if (e.key === 'Enter' && q) { e.preventDefault(); if (results[0] && exact) addAlso({ modifier_id: results[0].id, name: results[0].name, price: results[0].price, qty: 1 }); else addAlso({ custom: true, name: q, price: 0, qty: 1 }); } }} />
                  {(results.length > 0 || q) && (
                    <div className="divide-y divide-hairline border border-hairline bg-white">
                      {results.map((r) => (
                        <button key={r.id} type="button" onClick={() => addAlso({ modifier_id: r.id, name: r.name, price: r.price, qty: 1 })} className="flex min-h-[44px] w-full items-center justify-between gap-3 px-3 text-left text-[14px] hover:bg-surface-2" data-testid={`sheet-result-${r.id}`}>
                          <span>{r.name}{r.recent && <span className="ml-2 text-[10px] font-extrabold uppercase tracking-[0.05em] text-ink-faint">recent</span>}{r.revenue_line === 'food' && <span className="ml-2 text-[10px] font-extrabold uppercase tracking-[0.05em] text-signal-ok">food</span>}</span>
                          <span className="font-mono tabular-nums text-ink-soft">{p(r.price) === 0 ? '£0' : `${r.price > 0 ? '+' : '−'}${formatCurrency(Math.abs(r.price))}`}</span>
                        </button>
                      ))}
                      {q && !exact && (
                        <>
                          <button type="button" onClick={() => addAlso({ custom: true, name: q, price: 0, qty: 1 })} className="flex min-h-[44px] w-full items-center justify-between gap-3 bg-surface-2 px-3 text-left text-[14px] hover:bg-paper" data-testid="sheet-free-note">
                            <span>Add “{q}” as a note</span><span className="font-mono tabular-nums text-ink-soft">£0</span>
                          </button>
                          {config.free_text_pricing && (
                            <div className="grid grid-cols-[1fr_auto] items-center gap-2 bg-surface-2 py-1 pl-3 pr-1 text-[14px]" data-testid="sheet-free-priced">
                              <button type="button" onClick={() => addAlso({ custom: true, name: q, price: pounds(p(freePrice)), qty: 1 })} className="min-h-[44px] text-left hover:underline">
                                Add “{q}” as an extra
                                {overCap && <span className="ml-2 text-[10px] font-extrabold uppercase tracking-[0.05em] text-signal-warn">manager PIN over {formatCurrency(cap)}</span>}
                              </button>
                              <Stepper value={p(freePrice)} min={p(step)} onChange={(v) => setFreePrice(pounds(Math.max(p(step), v)))} format={(v) => `+${formatCurrency(pounds(v))}`} size={48} testId="sheet-free-price" />
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>}
          </div>
        </div>

        {/* the ticket, torn off */}
        <div className="grid gap-3 border-t border-dashed border-hairline-strong bg-paper px-[18px] pb-4 pt-3">
          <div className="grid gap-0.5 font-mono text-[12.5px] tabular-nums text-ink-soft" data-testid="sheet-ticket">
            <div className="flex justify-between font-bold text-ink"><span>1 {item?.name}</span><span>{formatCurrency(pounds(base + mealDelta + modifiers.filter((m) => m.option_id && m.group && modifierGroups.find((g) => g.label === m.group)?.rule === 'one').reduce((s, m) => s + m.price, 0)))}</span></div>
            {!changed && <div className="italic text-ink-faint">as it comes</div>}
            {/* A meal's parts, under its name: one line on the bill, three things made. */}
            {components.map((c) => (
              <div key={`${c.group}-${c.menu_item_id}`} className="flex justify-between pl-3">
                <span>{(c.quantity || 1) > 1 ? `${c.quantity}× ${c.name}` : c.name}</span>
                <span>{p(c.price_delta) ? `+${formatCurrency(pounds(p(c.price_delta) * (c.quantity || 1)))}` : ''}</span>
              </div>
            ))}
            {modifiers.map((m, i) => {
              const isDrinkPart = m.option_id && modifierGroups.find((g) => g.label === m.group)?.rule === 'one';
              return (
                <div key={i} className={cn('flex justify-between pl-3', m.custom && !p(m.price) && 'font-extrabold text-ink')}>
                  <span>{words[i]}</span><span>{isDrinkPart || p(m.price) === 0 ? '' : formatCurrency(pounds(m.price))}</span>
                </div>
              );
            })}
            {/* The message reads on the ticket the way it reads on the paper: the
                instruction quietly, the allergy loudest. */}
            {note && <div className="pl-3 text-ink">* {note}</div>}
            {allergyNote && <div className="pl-3 font-extrabold text-signal-stop">! {allergyNote}</div>}
          </div>
          {/* Off right now (a Sunday roast on a Tuesday): holding the card still opens this,
              for what's in it, but nothing new goes on an order. A line already on one keeps
              its Update: last orders at 15:59 is still a sale. */}
          {item?.available_now === false && !initial ? (
            <div className="flex min-h-[52px] items-center gap-2 border border-signal-warn-line bg-signal-warn-tint px-4 text-[14px] font-semibold text-signal-warn" data-testid="modifier-sheet-off">
              <AlertTriangle size={16} className="shrink-0" />
              {item.hours ? `On ${item.hours}. It can't be added now.` : "Not on right now. It can't be added."}
            </div>
          ) : (
          <button type="button" onClick={confirm} className="flex min-h-[52px] items-center justify-between bg-verlo px-4 font-bold text-white hover:bg-verlo-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-verlo focus-visible:ring-offset-2" data-testid="modifier-sheet-confirm">
            <span>{confirmLabel}</span><span className="font-mono text-[17px] font-extrabold tabular-nums">{formatCurrency(pounds(total))}</span>
          </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

/** A tap opens the sheet rather than dropping the item straight on the pad. A meal always
 *  does: nobody can sell a kids meal without saying which main. */
export const needsSheet = (item) => !!(item?.always_ask)
  || !!(item?.modifier_groups?.length) || !!(item?.meal_groups?.length) || !!sizeGroupOf(item);
export const drinkLike = isDrinkCategory;

export default ModifierSheet;
